// src/pages/Dashboard.js
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Play, Square, RefreshCw, CheckCircle, XCircle, AlertCircle, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { getStats, getHealth, getPipelineStatus, triggerRun, startScheduler, stopScheduler } from '../utils/api';

export default function Dashboard() {
  const qc = useQueryClient();
  const [dryRun, setDryRun] = useState(false);

  const { data: stats } = useQuery({ queryKey: ['stats'], queryFn: getStats, refetchInterval: 15000 });
  const { data: health } = useQuery({ queryKey: ['health'], queryFn: getHealth, refetchInterval: 30000 });
  const { data: pipelineStatus } = useQuery({ queryKey: ['pipeline-status'], queryFn: getPipelineStatus, refetchInterval: 5000 });

  const runMutation = useMutation({
    mutationFn: () => triggerRun({ lookbackMinutes: 60, dryRun }),
    onSuccess: (data) => {
      toast.success(`Pipeline complete — ${data.processed || 0} report(s) processed`);
      qc.invalidateQueries({ queryKey: ['stats'] });
      qc.invalidateQueries({ queryKey: ['runs'] });
    },
    onError: (err) => toast.error('Pipeline failed: ' + (err.response?.data?.error || err.message)),
  });

  const startMutation = useMutation({
    mutationFn: () => startScheduler(),
    onSuccess: () => { toast.success('Scheduler started'); qc.invalidateQueries({ queryKey: ['pipeline-status'] }); },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to start scheduler'),
  });

  const stopMutation = useMutation({
    mutationFn: stopScheduler,
    onSuccess: () => { toast.success('Scheduler stopped'); qc.invalidateQueries({ queryKey: ['pipeline-status'] }); },
  });

  const HEALTH_LABELS = {
    server: 'API Server',
    anthropicApiKey: 'Claude API Key',
    gmailConfigured: 'Gmail OAuth',
    driveConfigured: 'Google Drive',
    recipientsConfigured: 'Email Recipients',
  };

  const isRunning = pipelineStatus?.pipelineRunning;
  const schedulerActive = pipelineStatus?.scheduler?.running;

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">QuickBooks AI Reporting Automation — Overview</p>
      </div>

      {/* Stats */}
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-label">Total Runs</div>
          <div className="stat-value">{stats?.totalRuns ?? '—'}</div>
          <div className="stat-sub">All time</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Succeeded</div>
          <div className="stat-value" style={{ color: 'var(--green)' }}>{stats?.succeeded ?? '—'}</div>
          <div className="stat-sub">Reports processed</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Failed</div>
          <div className="stat-value" style={{ color: stats?.failed > 0 ? 'var(--red)' : 'var(--text)' }}>{stats?.failed ?? '—'}</div>
          <div className="stat-sub">Errors</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Tokens Used</div>
          <div className="stat-value">{stats?.totalTokensUsed?.toLocaleString() ?? '—'}</div>
          <div className="stat-sub">≈ ${stats?.estimatedCost ?? '0.00'} total</div>
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div className="card">
          <div className="card-title">Manual Trigger</div>
          <p className="text-muted mb-4">Poll Gmail now and process any new QuickBooks report emails.</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary"
              onClick={() => runMutation.mutate()}
              disabled={runMutation.isPending || isRunning}
            >
              {runMutation.isPending || isRunning
                ? <><span className="spinner" /> Running…</>
                : <><Play size={15} /> Run Pipeline</>}
            </button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer' }}>
              <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} />
              Dry run (skip Drive & email)
            </label>
          </div>
        </div>

        <div className="card">
          <div className="card-title">Scheduler</div>
          <p className="text-muted mb-4">
            {schedulerActive
              ? <>Active — <span className="mono">{pipelineStatus?.scheduler?.cron}</span></>
              : 'Not running — click Start to enable automatic weekly runs.'}
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              className="btn btn-primary"
              onClick={() => startMutation.mutate()}
              disabled={schedulerActive || startMutation.isPending}
            >
              <Play size={15} /> Start
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => stopMutation.mutate()}
              disabled={!schedulerActive}
            >
              <Square size={15} /> Stop
            </button>
          </div>
        </div>
      </div>

      {/* System Health */}
      <div className="card">
        <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          System Health
          <button className="btn btn-secondary btn-sm" onClick={() => qc.invalidateQueries({ queryKey: ['health'] })}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        {health ? (
          <div className="health-grid">
            {Object.entries(health.checks || {}).map(([key, ok]) => (
              <div key={key} className="health-item">
                <div className={`health-dot ${ok === true || ok === 'ok' ? 'ok' : 'err'}`} />
                <span style={{ flex: 1, fontSize: 13 }}>{HEALTH_LABELS[key] || key}</span>
                {ok === true || ok === 'ok'
                  ? <CheckCircle size={14} color="var(--green)" />
                  : <XCircle size={14} color="var(--red)" />}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state"><span className="spinner" /></div>
        )}
      </div>
    </div>
  );
}
